interface VariableMetrics {
  rmse?: number;
  mae?: number;
  quantile_05?: number;
  quantile_25?: number;
  quantile_50?: number;
  quantile_75?: number;
  quantile_95?: number;
}

interface StationMetrics {
  prediction_timedelta: number;
  metrics: Record<string, VariableMetrics>;
  sample_count: number;
}

interface StationMetricsResponse {
  data: StationMetrics[];
}

export interface BenchmarkQuery {
  station_ids?: string[];
  model?: string;
  start_time?: string;
  end_time?: string;
}

export interface ApiResponse<T> {
  data?: T;
  error?: string;
}

export class BenchmarkService {
  private baseUrl: string;
  private getToken: () => Promise<string>;

  constructor(baseUrl: string, getToken: () => Promise<string>) {
    this.baseUrl = baseUrl;
    this.getToken = getToken;
  }

  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {},
  ): Promise<ApiResponse<T>> {
    try {
      const token = await this.getToken();

      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          ...options.headers,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      return { data };
    } catch (error) {
      console.error(`Benchmark service error for ${endpoint}:`, error);
      return {
        error: error instanceof Error ? error.message : 'An unknown error occurred',
      };
    }
  }

  async getStationMetrics(params: BenchmarkQuery): Promise<ApiResponse<StationMetricsResponse>> {
    // Use POST endpoint with request body instead of GET with query parameters
    const endpoint = `/v1/station-benchmarks/metrics`;

    return this.makeRequest<StationMetricsResponse>(endpoint, {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }

  async getAvailableDates(
    model?: string,
    daysLookback: number = 365,
  ): Promise<ApiResponse<{ dates: string[] }>> {
    // Build query parameters
    const params = new URLSearchParams();
    if (model) {
      params.append('model', model);
    }
    params.append('days_lookback', daysLookback.toString());

    const endpoint = `/v1/station-benchmarks/available-dates?${params.toString()}`;

    return this.makeRequest<{ dates: string[] }>(endpoint, {
      method: 'GET',
    });
  }
}

export type { StationMetrics, VariableMetrics, StationMetricsResponse };
